/** @file hiddenFor: the nodes the viewport hides: isolate's, and the tree's eye with what hangs below it. */
import { treeUi } from '../tree/treeUi';
import { descendantsOf } from './descendantsOf';
import { hiddenForIsolate } from './hiddenForIsolate';
import type { ViewportCtx } from './viewportCtx';

/** The last answer per viewport, with the inputs it was made from. */
type Memo = { isolated: readonly string[]; view: ReadonlySet<string>; nodes: object; hidden: readonly string[] };

const memo = new WeakMap<ViewportCtx, Memo>();

/** `isolated` with every name of `view` that is a document node and the nodes below each. */
function merged(isolated: readonly string[], view: ReadonlySet<string>, nodes: Readonly<Record<string, { parent?: string }>>): string[] {
  const all = new Set(isolated);
  for (const name of [...view].filter((n) => Object.hasOwn(nodes, n))) {
    all.add(name);
    for (const below of descendantsOf(nodes, name)) all.add(below);
  }
  return [...all];
}

/**
 * `hiddenForIsolate` plus every node the node tree's eye hides in the view (`treeUi(store).viewHidden`, view state:
 * never `node.hidden`, never undo) and the nodes below it, so the joint, its bone and its drawables leave the
 * viewport together. The same array is returned while the inputs are unchanged (the drawn clip is memoised on it).
 */
export function hiddenFor(ctx: ViewportCtx): readonly string[] {
  const isolated = hiddenForIsolate(ctx);
  const view = treeUi(ctx.store).getState().viewHidden;
  const nodes = ctx.store.getState().puppet.doc?.puppet.nodes;
  if (view.size === 0 || nodes === undefined) return isolated;
  const hit = memo.get(ctx);
  if (hit?.isolated === isolated && hit.view === view && hit.nodes === nodes) return hit.hidden;
  const hidden = merged(isolated, view, nodes);
  memo.set(ctx, { isolated, view, nodes, hidden });
  return hidden;
}
